import React, { useEffect, useState, useCallback } from 'react';
import { 
  CheckSquare, 
  Plus, 
  Trash2,
  Circle,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Edit2,
  Flame,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { tasksApi, goalsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate, getPriorityColor } from '../lib/utils';
import { toast } from 'sonner';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [editingTask, setEditingTask] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    end_date: '',
    goal_id: '',
    recurring: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [tasksRes, goalsRes, dailyRes] = await Promise.all([
        tasksApi.getAll(),
        goalsApi.getAll().catch(() => ({ data: [] })),
        tasksApi.getDailySummary().catch(() => ({ data: null })),
      ]);
      setTasks(tasksRes.data || []);
      setGoals(goalsRes.data || []);
      setDailySummary(dailyRes.data || null);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({ title: '', description: '', priority: 'medium', due_date: '', end_date: '', goal_id: '', recurring: '' });
    setEditingTask(null);
  };

  const handleCompleteDailyTask = async (taskId) => {
    try {
      await tasksApi.completeToday(taskId);
      toast.success('Daily task completed!');
      loadData();
    } catch (error) {
      if (error.response?.data?.detail === 'Task already completed today') {
        toast.info('Task already completed today');
      } else {
        toast.error('Failed to complete task');
      }
    }
  };

  const handleUncompleteDailyTask = async (taskId) => {
    try {
      await tasksApi.uncompleteToday(taskId);
      toast.success('Completion removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove completion');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        // Update existing task
        await tasksApi.update(editingTask.id, {
          ...formData,
          goal_id: formData.goal_id || null,
          recurring: formData.recurring || null,
        });
        setTasks(prev => prev.map(t => 
          t.id === editingTask.id ? { ...t, ...formData } : t
        ));
        toast.success('Task updated!');
      } else {
        // Create new task
        const response = await tasksApi.create({
          ...formData,
          goal_id: formData.goal_id || null,
          recurring: formData.recurring || null,
        });
        setTasks(prev => [...prev, response.data]);
        toast.success('Task created!');
      }
      setShowForm(false);
      resetForm();
    } catch (error) {
      toast.error(editingTask ? 'Failed to update task' : 'Failed to create task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date || '',
      end_date: task.end_date || '',
      goal_id: task.goal_id || '',
      recurring: task.recurring || '',
    });
    setShowForm(true);
  };

  const handleStatusChange = async (taskId, status) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      await tasksApi.update(taskId, { status });
      
      // If completing a recurring task, reload to show the new task
      if (status === 'done' && task?.recurring) {
        await loadData();
        toast.success(`Task completed! Next ${task.recurring} task created.`);
      } else {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status } : t));
        toast.success(status === 'done' ? 'Task completed!' : 'Task updated');
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await tasksApi.delete(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (activeTab === 'all') return true;
    if (activeTab === 'todo') return task.status === 'todo';
    if (activeTab === 'in_progress') return task.status === 'in_progress';
    if (activeTab === 'done') return task.status === 'done';
    return true;
  });

  const getGoalName = (goalId) => {
    const goal = goals.find(g => g.id === goalId);
    return goal?.title || null;
  };

  const taskCounts = {
    all: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    done: tasks.filter(t => t.status === 'done').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="tasks-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Tasks
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage your daily operations and connect tasks to strategic goals.
          </p>
        </div>
        <Dialog open={showForm} onOpenChange={(open) => {
          setShowForm(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2" data-testid="create-task-btn">
              <Plus className="w-4 h-4" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
            <DialogHeader>
              <DialogTitle className="text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="What needs to be done?"
                  required
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="task-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Description (optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add more details..."
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="task-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300">Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                      <SelectItem value="low" className="text-slate-800 dark:text-slate-100">Low</SelectItem>
                      <SelectItem value="medium" className="text-slate-800 dark:text-slate-100">Medium</SelectItem>
                      <SelectItem value="high" className="text-slate-800 dark:text-slate-100">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300">Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    data-testid="task-duedate-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <RefreshCw className="w-4 h-4" />
                  Repeat (Recurring)
                </Label>
                <Select 
                  value={formData.recurring || 'none'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, recurring: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid="task-recurring-select">
                    <SelectValue placeholder="No repeat" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                    <SelectItem value="none" className="text-slate-800 dark:text-slate-100">No repeat</SelectItem>
                    <SelectItem value="daily" className="text-slate-800 dark:text-slate-100">Daily</SelectItem>
                    <SelectItem value="weekly" className="text-slate-800 dark:text-slate-100">Weekly</SelectItem>
                    <SelectItem value="monthly" className="text-slate-800 dark:text-slate-100">Monthly</SelectItem>
                  </SelectContent>
                </Select>
                {formData.recurring === 'daily' && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Track daily completion and view progress percentage.
                  </p>
                )}
              </div>

              {/* End Date for Daily Tasks */}
              {formData.recurring === 'daily' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <Calendar className="w-4 h-4" />
                    End Date (optional)
                  </Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    data-testid="task-end-date-input"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {formData.end_date 
                      ? 'Progress will be calculated until this date' 
                      : 'Without end date, progress shows days complete vs incomplete'}
                  </p>
                </div>
              )}

              {goals.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-300">Link to Goal (optional)</Label>
                  <Select 
                    value={formData.goal_id || 'none'} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, goal_id: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid="task-goal-select">
                      <SelectValue placeholder="Select a goal" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                      <SelectItem value="none" className="text-slate-800 dark:text-slate-100">None</SelectItem>
                      {goals.map(goal => (
                        <SelectItem key={goal.id} value={goal.id} className="text-slate-800 dark:text-slate-100">{goal.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                  className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937]"
                  data-testid="cancel-task-btn"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full"
                  data-testid="save-task-btn"
                >
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white dark:bg-[#111827] p-1 rounded-full border border-slate-200 dark:border-[#1F2937]">
          <TabsTrigger 
            value="all" 
            className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#26C6DA] data-[state=active]:to-[#8B5CF6] data-[state=active]:text-black text-slate-500 dark:text-slate-400"
            data-testid="tab-all"
          >
            All ({taskCounts.all})
          </TabsTrigger>
          <TabsTrigger 
            value="todo" 
            className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#26C6DA] data-[state=active]:to-[#8B5CF6] data-[state=active]:text-black text-slate-500 dark:text-slate-400"
            data-testid="tab-todo"
          >
            To Do ({taskCounts.todo})
          </TabsTrigger>
          <TabsTrigger 
            value="in_progress" 
            className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#26C6DA] data-[state=active]:to-[#8B5CF6] data-[state=active]:text-black text-slate-500 dark:text-slate-400"
            data-testid="tab-in-progress"
          >
            In Progress ({taskCounts.in_progress})
          </TabsTrigger>
          <TabsTrigger 
            value="done" 
            className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#26C6DA] data-[state=active]:to-[#8B5CF6] data-[state=active]:text-black text-slate-500 dark:text-slate-400"
            data-testid="tab-done"
          >
            Done ({taskCounts.done})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredTasks.length > 0 ? (
            <div className="space-y-3">
              {filteredTasks.map((task, index) => {
                // Check if this is a daily task completed today
                const dailyStats = task.recurring === 'daily' ? dailySummary?.tasks?.find(t => t.task_id === task.id) : null;
                const isCompletedToday = dailyStats?.completed_today;
                
                return (
                <div 
                  key={task.id}
                  className={`relative bg-white dark:bg-[#111827] backdrop-blur-md border rounded-xl p-4 flex items-start gap-4 transition-all hover:border-[#26C6DA]/30 ${
                    task.status === 'done' ? 'opacity-60 border-slate-200 dark:border-[#1F2937]' : 
                    isCompletedToday ? 'border-emerald-500/50 bg-emerald-500/5 dark:bg-emerald-500/10' : 
                    'border-slate-200 dark:border-[#1F2937]'
                  }`}
                  data-testid={`task-item-${index}`}
                >
                  {/* Completed Today Indicator - Large checkmark for daily tasks */}
                  {isCompletedToday && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full shadow-lg" data-testid={`task-completed-badge-${index}`}>
                      <CheckCircle className="w-4 h-4" />
                      <span>Done Today</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleStatusChange(task.id, task.status === 'done' ? 'todo' : 'done')}
                    className="flex-shrink-0 mt-1"
                    data-testid={`task-toggle-${index}`}
                  >
                    {task.status === 'done' ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-500 hover:text-[#26C6DA]" />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className={`font-medium text-slate-800 dark:text-slate-100 ${task.status === 'done' ? 'line-through' : ''}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{task.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                          <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{task.priority}</span>
                          {task.due_date && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                              <Clock className="w-3 h-3" />
                              {formatDate(task.due_date)}
                            </div>
                          )}
                          {task.recurring && (
                            <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">
                              <RefreshCw className="w-3 h-3" />
                              {task.recurring}
                            </div>
                          )}
                          {/* Daily task progress and complete button */}
                          {task.recurring === 'daily' && (() => {
                            return (
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Progress percentage */}
                                {dailyStats && (
                                  <>
                                    <div className="flex items-center gap-1 text-xs">
                                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                                      <span className="text-emerald-400 font-medium">{dailyStats.completion_percentage}%</span>
                                      <span className="text-slate-500">({dailyStats.completed_days}/{dailyStats.total_days} days)</span>
                                    </div>
                                    {dailyStats.current_streak > 0 && (
                                      <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                                        <Flame className="w-3 h-3" />
                                        {dailyStats.current_streak} day streak
                                      </div>
                                    )}
                                  </>
                                )}
                                {/* Complete Today / Undo button */}
                                {isCompletedToday ? (
                                  <button
                                    onClick={() => handleUncompleteDailyTask(task.id)}
                                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-500/20 text-slate-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                                    data-testid={`task-daily-undo-${index}`}
                                  >
                                    Undo
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleCompleteDailyTask(task.id)}
                                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors font-medium shadow-sm"
                                    data-testid={`task-daily-complete-${index}`}
                                  >
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    Complete Today
                                  </button>
                                )}
                                {/* End date indicator */}
                                {task.end_date && (
                                  <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                    <Calendar className="w-3 h-3" />
                                    Ends {formatDate(task.end_date)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {task.goal_id && getGoalName(task.goal_id) && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#26C6DA]/10 text-[#26C6DA]">
                              {getGoalName(task.goal_id)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {task.status !== 'done' && (
                          <Select 
                            value={task.status} 
                            onValueChange={(value) => handleStatusChange(task.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100" data-testid={`task-status-${index}-select`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
                              <SelectItem value="todo" className="text-slate-800 dark:text-slate-100">To Do</SelectItem>
                              <SelectItem value="in_progress" className="text-slate-800 dark:text-slate-100">In Progress</SelectItem>
                              <SelectItem value="done" className="text-slate-800 dark:text-slate-100">Done</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <button
                          onClick={() => handleEdit(task)}
                          className="p-2 text-slate-500 hover:text-[#26C6DA] transition-colors"
                          data-testid={`task-edit-${index}-btn`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(task.id)}
                          className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                          data-testid={`task-delete-${index}-btn`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          ) : (
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-12 text-center">
              <CheckSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                {activeTab === 'all' ? 'No Tasks Yet' : `No ${activeTab.replace('_', ' ')} Tasks`}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {activeTab === 'all' 
                  ? 'Create your first task to start managing your operations.'
                  : 'Tasks in this status will appear here.'}
              </p>
              {activeTab === 'all' && (
                <Button 
                  onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2"
                  data-testid="empty-create-task-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Task
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Tasks;
